import { computed, InjectionToken, Signal } from '@angular/core';
import { DetailPaneNavigation } from './detail.pane.navigation';

/**
 * The "back to parent" affordance for a surface mounted as a child inside a detail pane.
 *
 * Back is header navigation, not a surface action: it sits leftmost on the title
 * row, before the title. Pin and close stay in the trailing chrome slot. Rendering
 * it as a separate row above the child surface pushed the whole header down.
 *
 * A token plus an interface, matching `BOARD_PANEL_HOST`: the value is built per mount, so there is
 * no injectable class to implement, and the child surface must degrade cleanly everywhere no pane
 * exists (dialogs, pages, board panels). Always inject it `{ optional: true }`.
 *
 * Provided by `DetailPaneComponent` for its child pane's subtree only — the root pane has no parent
 * to go back to, so nothing above a child ever sees it.
 */
export interface DetailPaneBack {

  /** Label for the control, e.g. "Back to Visit". Also its tooltip. */
  readonly $label: Signal<string>;

  /** Returns to the parent record. False when a deactivate guard refused the leave. */
  back(): Promise<boolean>;
}

export const DETAIL_PANE_BACK = new InjectionToken<DetailPaneBack>('DetailPaneBack');

/**
 * Builds the value the token carries, given the ENCLOSING pane's navigation.
 *
 * Null in means null out: a root pane has nothing above it to return to, and its surface must then
 * render no Back at all rather than a control that would close the pane or do nothing. `back()`
 * delegates to the navigation so the parent's leave guard still runs and a child with pending edits
 * can refuse the trip.
 */
export function detailPaneBackFor(parent: DetailPaneNavigation | null): DetailPaneBack | null {

  if (!parent) return null;
  return { $label: computed(() => parent.$target()?.backLabel ?? 'Back'), back: () => parent.back() };
}
